// src/components/EventDetail.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';
import QrCodeCard from './QrCodeCard';

// Default-Basis-URL für die öffentliche Eventseite
const DEFAULT_PUBLIC_EVENT_BASE = 'https://digitecstudieren.de/events/event.html';

export default function EventDetail({ id, onBack, onEdit, onDeleted }){
  const [ev, setEv]       = useState(null);
  const [items, setItems] = useState([]);
  const [err, setErr]     = useState('');
  const [loading, setLoading] = useState(false);

  // QR/UTM Settings
  const [utmOn, setUtmOn]             = useState(true);
  const [utmMedium, setUtmMedium]     = useState('print');
  const [utmSource, setUtmSource]     = useState('qr');
  const [utmCampaign, setUtmCampaign] = useState('event');
  const [pngSize, setPngSize]         = useState(1024);

  async function load(){
    try{
      setLoading(true); setErr('');
      const [d1, d2] = await Promise.all([
        apiFetch(`admin/event_detail.php?id=${id}`).then(r=>r.json()),
        apiFetch(`admin/registrations_list.php?event_id=${id}`).then(r=>r.json()),
      ]);
      if (!d1.ok) throw new Error(d1.error || 'Fehler (event)');
      if (!d2.ok) throw new Error(d2.error || 'Fehler (regs)');
      setEv(d1.event);
      setItems(d2.items || []);
      if (d1.event?.slug) setUtmCampaign(d1.event.slug);
    }catch(e){
      setErr(e.message || 'Laden fehlgeschlagen');
    }finally{
      setLoading(false);
    }
  }
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [id]);

  // Öffentlicher Link (ohne Tracking)
  const nakedUrl = useMemo(()=>{
    if (!ev?.slug) return '';
    const base = (ev.public_base_url && ev.public_base_url.trim()) || DEFAULT_PUBLIC_EVENT_BASE;
    const u = new URL(base);
    u.searchParams.set('slug', ev.slug);
    return u.toString();
  }, [ev]);

  // Mit UTM-Tracking
  const trackedUrl = useMemo(()=>{
    if (!nakedUrl) return '';
    if (!utmOn) return nakedUrl;
    const u = new URL(nakedUrl);
    u.searchParams.set('utm_source',   utmSource   || 'qr');
    u.searchParams.set('utm_medium',   utmMedium   || 'print');
    u.searchParams.set('utm_campaign', utmCampaign || (ev?.slug || 'event'));
    return u.toString();
  }, [nakedUrl, utmOn, utmSource, utmMedium, utmCampaign, ev]);

  // CSV Export
  const exportCsv = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const base   = origin.replace(/#.*$/,'');
    const url    = `${base}/backend-web/admin/registrations_export_csv.php?event_id=${id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = (text)=>{
    if (!text) return;
    navigator.clipboard?.writeText(text);
  };

  // Löschen (nur wenn keine Registrierungen existieren)
  async function handleDelete(){
    if (!ev) return;
    if ((items?.length || 0) > 0) {
      alert('Dieses Event hat bereits Anmeldungen und kann nicht gelöscht werden.');
      return;
    }
    const ok = window.confirm(`Event „${ev.title}“ wirklich löschen? Dies kann nicht rückgängig gemacht werden.`);
    if (!ok) return;

    try{
      setErr('');
      const res = await apiFetch('admin/event_delete.php', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ id })
      });
      const js = await res.json();
      if (!js.ok) throw new Error(js.error || 'Löschen fehlgeschlagen');
      onDeleted?.();
    }catch(e){
      setErr(e.message || 'Löschen fehlgeschlagen');
    }
  }

  return (
    <div>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}}>
        <button className="secondary" onClick={onBack}>← Zurück</button>
        <div style={{display:'flex', gap:8}}>
          <button className="secondary" onClick={()=>onEdit?.(id)} disabled={!ev}>Bearbeiten</button>
          <button
            className="danger"
            onClick={handleDelete}
            title={(items?.length||0)>0 ? 'Event mit bestehenden Anmeldungen kann nicht gelöscht werden' : 'Event löschen'}
            disabled={!ev || (items?.length||0)>0}
          >
            Löschen
          </button>
        </div>
      </div>

      <h2 style={{marginTop:8}}>Event: {ev?.title || '—'}</h2>

      {err && <div className="card" style={{color:'crimson', borderColor:'crimson'}}>{err}</div>}
      {loading && <div className="card">Lade…</div>}

      {ev && (
        <>
          <div className="cards" style={{display:'flex', gap:12, flexWrap:'wrap', marginBottom:12}}>
            <div className="card"><div>Slug</div><div style={{fontWeight:700}}>{ev.slug}</div></div>
            <div className="card"><div>Anmeldungen</div><div style={{fontSize:'1.4rem', fontWeight:700}}>{ev.registrations}</div></div>
            <div className="card"><div>Kapazität</div><div>{ev.capacity ?? '—'}</div></div>
            <div className="card"><div>Reg.-Fenster</div><div>{ev.registration_start_at || '—'} – {ev.registration_end_at || '—'}</div></div>
          </div>

          {/* Öffentlicher Link */}
          <div className="card" style={{marginBottom:12}}>
            <h3 style={{marginTop:0}}>Öffentlicher Link</h3>

            <div style={{display:'grid', gap:10}}>
              <div>
                <div style={{fontSize:12, opacity:.7, marginBottom:4}}>Ohne Tracking</div>
                <div style={{display:'grid', gridTemplateColumns:'1fr auto auto', gap:8}}>
                  <input readOnly value={nakedUrl} />
                  <button className="secondary" onClick={()=>copyToClipboard(nakedUrl)} disabled={!nakedUrl}>Kopieren</button>
                  <a className="primary" href={nakedUrl || '#'} target="_blank" rel="noreferrer">Öffnen</a>
                </div>
              </div>

              <div style={{marginTop:8}}>
                <label style={{display:'flex', alignItems:'center', gap:8}}>
                  <input type="checkbox" checked={utmOn} onChange={e=>setUtmOn(e.target.checked)} />
                  UTM-Tracking anhängen
                </label>

                <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginTop:8, opacity: utmOn ? 1 : .5}}>
                  <div>
                    <div style={{fontSize:12, opacity:.7}}>utm_source</div>
                    <input value={utmSource} onChange={e=>setUtmSource(e.target.value)} disabled={!utmOn}/>
                  </div>
                  <div>
                    <div style={{fontSize:12, opacity:.7}}>utm_medium</div>
                    <input value={utmMedium} onChange={e=>setUtmMedium(e.target.value)} disabled={!utmOn}/>
                  </div>
                  <div>
                    <div style={{fontSize:12, opacity:.7}}>utm_campaign</div>
                    <input value={utmCampaign} onChange={e=>setUtmCampaign(e.target.value)} disabled={!utmOn}/>
                  </div>
                </div>

                <div style={{marginTop:8}}>
                  <div style={{fontSize:12, opacity:.7, marginBottom:4}}>Mit Tracking</div>
                  <div style={{display:'grid', gridTemplateColumns:'1fr auto auto', gap:8}}>
                    <input readOnly value={trackedUrl} />
                    <button className="secondary" onClick={()=>copyToClipboard(trackedUrl)} disabled={!trackedUrl}>Kopieren</button>
                    <a className="primary" href={trackedUrl || '#'} target="_blank" rel="noreferrer">Öffnen</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* QR-Code */}
          <div className="card" style={{marginBottom:12}}>
            <h3 style={{marginTop:0}}>QR-Code</h3>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
              <div className="card" style={{padding:12}}>
                <div>
                  <div style={{fontSize:12, opacity:.7}}>PNG-Auflösung</div>
                  <select value={pngSize} onChange={e=>setPngSize(Number(e.target.value))}>
                    <option value={512}>512 px</option>
                    <option value={768}>768 px</option>
                    <option value={1024}>1024 px</option>
                    <option value={2048}>2048 px</option>
                  </select>
                  <div style={{fontSize:12, opacity:.65, marginTop:8}}>
                    Hinweis: Der QR nutzt aktuell den „Mit Tracking“-Link.
                  </div>
                </div>
              </div>

              <QrCodeCard
                url={trackedUrl}
                fileBase={`qr_${ev.slug}`}
                previewSize={256}
                pngSize={pngSize}
                ecLevel="M"
                margin={2}
              />
            </div>
          </div>

          {/* Registrierungen */}
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6}}>
            <h3>Registrierungen</h3>
            <button className="secondary" onClick={exportCsv}>CSV exportieren</button>
          </div>

          <div className="card">
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead>
                <tr style={{textAlign:'left', borderBottom:'1px solid #eee'}}>
                  <th>ID</th><th>Zeit</th><th>Name</th><th>E-Mail</th><th>Teilnahme</th><th>Weitere Daten</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r=>{
                  const mode = r.data?.mode || '';
                  const rest = {...r.data}; delete rest.mode;
                  return (
                    <tr key={r.id} style={{borderBottom:'1px solid #f1f1f1'}}>
                      <td>{r.id}</td>
                      <td>{r.created_at}</td>
                      <td>{r.full_name || `${r.data?.first_name||''} ${r.data?.last_name||''}`.trim()}</td>
                      <td>{r.email || r.data?.email || ''}</td>
                      <td>{mode}</td>
                      <td style={{fontSize:12, opacity:.8}}>
                        {Object.keys(rest).length===0 ? '—' :
                          Object.entries(rest).map(([k,v])=>(
                            <div key={k}><strong>{k}:</strong> {Array.isArray(v) ? v.join(' | ') : String(v)}</div>
                          ))}
                      </td>
                    </tr>
                  );
                })}
                {items.length===0 && (
                  <tr><td colSpan={6} style={{opacity:.7}}>Noch keine Registrierungen.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}